import { c as _c } from "react/compiler-runtime";
import React from "react";
import { SECTION_CONTAINER_WIDTHS, type SectionContainerWidth } from "@/components/atoms/section-constants";
import { SectionBackground } from "./Section/SectionBackground";
import { SectionContent } from "./Section/SectionContent";
import { BackgroundPatternType } from "@/components/atoms/BackgroundPattern";
import { isDarkColor } from "@/lib/colors";
import { cn } from "@/lib/utils";
import { ContentImage, ContentVideo } from "@/content/types";
import { StatCard } from "@/components/molecules/StatCard";
import type { StatisticItem } from "@/components/molecules/StatCard/StatCard.props";
import { Box } from "@/components/atoms/Box";
import { Text } from "@/components/atoms/Text";
interface PortableStatisticsProps {
  heading?: string;
  statistics: StatisticItem[];
  layout?: "row" | "grid" | "stacked" | "featured";
  style?: {
    backgroundColor?: string;
    backgroundImage?: ContentImage;
    backgroundVideo?: ContentVideo;
    backgroundPattern?: string;
    patternOpacity?: number;
    textColor?: string;
    paddingTop?: string;
    paddingBottom?: string;
    containerWidth?: string;
    customClasses?: string;
  };
}
const paddingMapping: Record<string, string> = {
  "pt-0": "pt-0",
  "pt-8": "pt-8",
  "pt-16": "pt-16",
  "pt-24": "pt-24",
  "pt-32": "pt-32",
  "pb-0": "pb-0",
  "pb-8": "pb-8",
  "pb-16": "pb-16",
  "pb-24": "pb-24",
  "pb-32": "pb-32"
};

/**
 * PortableStatistics Component
 *
 * A block for displaying groups of prominent statistics with source attribution.
 * Supports 4 layout variants: row, grid, stacked, and featured.
 *
 * Uses the StatCard molecule for individual statistic rendering.
 */
export function PortableStatistics(t0) {
  const $ = _c(68);
  const {
    heading,
    statistics,
    layout: t1,
    style
  } = t0;
  const layout = t1 === undefined ? "row" : t1;
  const bgColor = style?.backgroundColor || "off-white";
  const paddingTop = style?.paddingTop || "pt-16";
  const paddingBottom = style?.paddingBottom || "pb-16";
  const containerWidthValue = style?.containerWidth || "max-w-6xl";
  const ptClass = paddingMapping[paddingTop] || "pt-16";
  const pbClass = paddingMapping[paddingBottom] || "pb-16";
  const containerSize = (containerWidthValue in SECTION_CONTAINER_WIDTHS ? containerWidthValue : "max-w-7xl") as SectionContainerWidth;
  let t2;
  if ($[0] !== bgColor) {
    t2 = isDarkColor(bgColor);
    $[0] = bgColor;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const isDark = t2;
  if (!statistics || statistics.length === 0) {
    return null;
  }
  let t3;
  if ($[2] !== layout) {
    t3 = () => {
      switch (layout) {
        case "row":
          {
            return "flex flex-col md:flex-row md:justify-between gap-8 md:gap-12";
          }
        case "grid":
          {
            return "grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12";
          }
        case "stacked":
          {
            return "flex flex-col gap-10 max-w-2xl";
          }
        case "featured":
          {
            return "";
          }
        default:
          {
            return "flex flex-col md:flex-row md:justify-between gap-8 md:gap-12";
          }
      }
    };
    $[2] = layout;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const getLayoutClasses = t3;
  if (layout === "featured" && statistics.length > 1) {
    let featured;
    let rest;
    if ($[4] !== statistics) {
      [featured, ...rest] = statistics;
      $[4] = statistics;
      $[5] = featured;
      $[6] = rest;
    } else {
      featured = $[5];
      rest = $[6];
    }
    const t4 = heading || "Statistics";
    const t5 = style?.customClasses;
    let t6;
    if ($[7] !== pbClass || $[8] !== ptClass || $[9] !== t5) {
      t6 = cn("not-prose statistics-callout my-16", "rounded-2xl md:rounded-3xl border border-white/10 shadow-2xl", ptClass, pbClass, t5);
      $[7] = pbClass;
      $[8] = ptClass;
      $[9] = t5;
      $[10] = t6;
    } else {
      t6 = $[10];
    }
    const t7 = style?.backgroundImage;
    const t8 = style?.backgroundVideo;
    const t9 = style?.backgroundPattern as BackgroundPatternType;
    const t10 = style?.patternOpacity;
    let t11;
    if ($[11] !== heading) {
      t11 = heading && <Text as="p" className="text-2xl font-bold tracking-tight mb-10 opacity-80">{heading}</Text>;
      $[11] = heading;
      $[12] = t11;
    } else {
      t11 = $[12];
    }
    let t12;
    if ($[13] !== featured || $[14] !== isDark) {
      t12 = <Box className="mb-12 pb-12 border-b border-white/10"><StatCard stat={featured} isDark={isDark} size="large" /></Box>;
      $[13] = featured;
      $[14] = isDark;
      $[15] = t12;
    } else {
      t12 = $[15];
    }
    let t13;
    if ($[16] !== isDark || $[17] !== rest) {
      t13 = <Box className="flex flex-col md:flex-row md:justify-between gap-8 md:gap-12">{rest.map((stat, index) => <Box key={stat._key || index} className="flex-1"><StatCard stat={stat} isDark={isDark} size="normal" /></Box>)}</Box>;
      $[16] = isDark;
      $[17] = rest;
      $[18] = t13;
    } else {
      t13 = $[18];
    }
    let t14;
    if ($[19] !== t11 || $[20] !== t12 || $[21] !== t13) {
      t14 = <Box className="relative z-10">{t11}{t12}{t13}</Box>;
      $[19] = t11;
      $[20] = t12;
      $[21] = t13;
      $[22] = t14;
    } else {
      t14 = $[22];
    }
    let t15;
    if ($[23] !== containerSize || $[24] !== t14) {
      t15 = <SectionContent size={containerSize}>{t14}</SectionContent>;
      $[23] = containerSize;
      $[24] = t14;
      $[25] = t15;
    } else {
      t15 = $[25];
    }
    let t16;
    if ($[26] !== bgColor || $[27] !== isDark || $[28] !== t10 || $[29] !== t15 || $[30] !== t4 || $[31] !== t6 || $[32] !== t7 || $[33] !== t8 || $[34] !== t9) {
      t16 = <SectionBackground as="section" aria-label={t4} className={t6} backgroundColor={bgColor} backgroundImage={t7} backgroundVideo={t8} backgroundPattern={t9} patternOpacity={t10} isDark={isDark}>{t15}</SectionBackground>;
      $[26] = bgColor;
      $[27] = isDark;
      $[28] = t10;
      $[29] = t15;
      $[30] = t4;
      $[31] = t6;
      $[32] = t7;
      $[33] = t8;
      $[34] = t9;
      $[35] = t16;
    } else {
      t16 = $[35];
    }
    return t16;
  }
  const t4 = heading || "Statistics";
  const t5 = style?.customClasses;
  let t6;
  if ($[36] !== pbClass || $[37] !== ptClass || $[38] !== t5) {
    t6 = cn("not-prose statistics-callout my-16", "rounded-2xl md:rounded-3xl border border-white/10 shadow-2xl", ptClass, pbClass, t5);
    $[36] = pbClass;
    $[37] = ptClass;
    $[38] = t5;
    $[39] = t6;
  } else {
    t6 = $[39];
  }
  const t7 = style?.backgroundImage;
  const t8 = style?.backgroundVideo;
  const t9 = style?.backgroundPattern as BackgroundPatternType;
  const t10 = style?.patternOpacity;
  let t11;
  if ($[40] !== heading) {
    t11 = heading && <Text as="p" className="text-2xl font-bold tracking-tight mb-10 opacity-80">{heading}</Text>;
    $[40] = heading;
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  const t12 = getLayoutClasses();
  let t13;
  if ($[42] !== isDark || $[43] !== layout || $[44] !== statistics) {
    let t14;
    if ($[46] !== isDark || $[47] !== layout) {
      t14 = (stat_0, index_0) => <Box key={stat_0._key || index_0} className={layout === "row" ? "flex-1" : ""}><StatCard stat={stat_0} isDark={isDark} /></Box>;
      $[46] = isDark;
      $[47] = layout;
      $[48] = t14;
    } else {
      t14 = $[48];
    }
    t13 = statistics.map(t14);
    $[42] = isDark;
    $[43] = layout;
    $[44] = statistics;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  let t14;
  if ($[49] !== t12 || $[50] !== t13) {
    t14 = <Box className={t12}>{t13}</Box>;
    $[49] = t12;
    $[50] = t13;
    $[51] = t14;
  } else {
    t14 = $[51];
  }
  let t15;
  if ($[52] !== t11 || $[53] !== t14) {
    t15 = <Box className="relative z-10">{t11}{t14}</Box>;
    $[52] = t11;
    $[53] = t14;
    $[54] = t15;
  } else {
    t15 = $[54];
  }
  let t16;
  if ($[55] !== containerSize || $[56] !== t15) {
    t16 = <SectionContent size={containerSize}>{t15}</SectionContent>;
    $[55] = containerSize;
    $[56] = t15;
    $[57] = t16;
  } else {
    t16 = $[57];
  }
  let t17;
  if ($[58] !== bgColor || $[59] !== isDark || $[60] !== t10 || $[61] !== t16 || $[62] !== t4 || $[63] !== t6 || $[64] !== t7 || $[65] !== t8 || $[66] !== t9) {
    t17 = <SectionBackground as="section" aria-label={t4} className={t6} backgroundColor={bgColor} backgroundImage={t7} backgroundVideo={t8} backgroundPattern={t9} patternOpacity={t10} isDark={isDark}>{t16}</SectionBackground>;
    $[58] = bgColor;
    $[59] = isDark;
    $[60] = t10;
    $[61] = t16;
    $[62] = t4;
    $[63] = t6;
    $[64] = t7;
    $[65] = t8;
    $[66] = t9;
    $[67] = t17;
  } else {
    t17 = $[67];
  }
  return t17;
}
export default PortableStatistics;